"use client";

import { useEffect, useState } from "react";
import clsx from "clsx";
import type { LcIssuance } from "@/lib/sse/useSSE";
import { BankBadge } from "@/components/banks/BankBadge";
import { NetworkVignette, type Preset } from "@/components/web/NetworkVignette";
import type { GraphState } from "@/components/web/types";
import { BankerEvidence } from "@/components/scenes/shared/BankerEvidence";
import { VaultIllustration } from "@/components/scenes/shared/illustrations/VaultIllustration";
import { DigitalLcModal } from "@/components/scenes/act3/DigitalLcModal";
import { useUserActions } from "@/lib/userActions/UserActionsContext";
import { useTranslations } from "@/lib/compat/i18n";

/**
 * Screen 3.4 / 4.5 / 5.6 — LC Issued. Renders the LC reference + amount
 * + bank badge if applicable, plus the split-screen handshake panes for
 * T1's #5. A NetworkVignette below shows the LC node materialising on the
 * graph between buyer and seller (and the Bank node anchoring adjacent
 * for T2 / T3 partial-financed variants).
 */
/**
 * Default advising-bank mapping by issuing bank. In real LC mechanics the
 * issuing bank (buyer's bank) sends the LC to a correspondent bank in the
 * seller's jurisdiction, which then advises the seller. The seller's bank
 * is the "advising bank". This makes the chain explicit on screen so
 * finance audiences see the full SWIFT MT 700 → MT 710 progression.
 */
const ADVISING_BANK_BY_ISSUER: Record<string, string> = {
  FAB: "Banco do Brasil",   // T2 · FAB (UAE issuer) → BR-side advising bank to Aurora
  Itau: "Mashreq Bank",     // T3 · Itaú (BR issuer) → UAE-side advising bank to Gulf Phosphate
};

export function LcIssued({
  lc,
  handshakeNumber,
  buyerLabel = "Al Madar · Importer",
  sellerLabel = "Aurora · Exporter",
  advisingBank,
  confirmingBank,
  presentationPeriodDays = 21,
  showDigitalLcModal = false,
  digitalLcAutoOpenKey,
  graph,
  className,
}: {
  lc: LcIssuance | null;
  /** Only T1's LC carries one of the 5 sacred handshake badges (#5).
   *  T2 / T3 are split-screen but do not advance the handshake counter. */
  handshakeNumber?: number;
  buyerLabel?: string;
  sellerLabel?: string;
  /** Name of the seller-side advising bank. Defaults derived from
   *  ADVISING_BANK_BY_ISSUER. Not shown for Programmable Escrow (T1). */
  advisingBank?: string;
  /** Optional confirming bank — the bank that adds an independent payment
   *  undertaking on top of the issuing bank's. Most real LCs ship without
   *  confirmation; supplied only when the scenario specifically models a
   *  three-party LC. Not surfaced on Programmable Escrow (no bank chain). */
  confirmingBank?: string;
  /** UCP 600 stipulates the document presentation period in days from
   *  shipment date. Default 21 — standard for most documentary credits. */
  presentationPeriodDays?: number;
  /** When true, mounts a digital-LC popup (SWIFT MT 700 fields) and
   *  surfaces a "View digital LC" button that re-opens it. Auto-opens
   *  once on first mount; persistence is keyed by `digitalLcAutoOpenKey`. */
  showDigitalLcModal?: boolean;
  digitalLcAutoOpenKey?: string;
  graph?: GraphState;
  className?: string;
}) {
  const userActions = useUserActions();
  const autoOpened = digitalLcAutoOpenKey ? userActions.has(digitalLcAutoOpenKey) : false;
  const [digitalLcOpen, setDigitalLcOpen] = useState(false);
  const t = useTranslations("lcIssued");

  useEffect(() => {
    if (!showDigitalLcModal) return;
    if (autoOpened) return;
    setDigitalLcOpen(true);
    if (digitalLcAutoOpenKey) userActions.fire(digitalLcAutoOpenKey);
  }, [showDigitalLcModal, autoOpened, digitalLcAutoOpenKey, userActions]);

  if (!lc) {
    return (
      <div className={clsx("grid h-full place-items-center", className)}>
        <p className="font-display text-base font-bold text-orbitra-paper">{t("issuancePendingChip")}</p>
      </div>
    );
  }

  const bankName = lc.buyerSideBankBadge;
  const fundingPct = lc.fundingMix?.bankFinancingPct;
  // Advising bank applies to bank-LC scenarios only — Programmable Escrow
  // skips the SWIFT chain entirely.
  const resolvedAdvisingBank = bankName ? (advisingBank ?? ADVISING_BANK_BY_ISSUER[bankName]) : undefined;
  return (
    <div className={clsx("relative flex h-full flex-col gap-4", className)}>
      <div className="glass p-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-financed">{bankName ? t("kickerLc") : t("kickerEscrow")}</p>
            <h2 className="mt-1 font-display text-2xl font-semibold text-orbitra-paper" dir="ltr">{lc.lcReference}</h2>
            <p className="mt-1 text-[12px] text-orbitra-teal-soft">USD {lc.amountUsd.toLocaleString()} · {t("expiryPrefix")} {lc.expiry}</p>
            {resolvedAdvisingBank && (
              <p
                className="mt-1 font-mono text-[10px] text-orbitra-teal-soft cursor-help"
                title={`Issuing bank → advising bank chain: ${bankName} sends MT 700 to ${resolvedAdvisingBank}, which transmits MT 710 to the seller.${confirmingBank ? ` Confirming bank ${confirmingBank} adds an independent payment undertaking.` : ""}`}
              >
                {t("issuedByPrefix")} <span className="text-state-financed">{bankName}</span> {t("advisedViaPrefix")} <span className="text-state-financed">{resolvedAdvisingBank}</span>
                {confirmingBank && (
                  <> {t("confirmedByPrefix")} <span className="text-state-financed">{confirmingBank}</span></>
                )}
              </p>
            )}
          </div>
          <div className="flex flex-col items-end gap-2">
            {bankName ? (
              <BankBadge name={bankName} fundingPct={fundingPct ?? undefined} variant="edge" />
            ) : (
              <span className="rounded-full border border-state-financed/40 bg-state-financed/10 px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-state-financed shadow-[0_0_18px_var(--state-financed-glow)]">
                {t("noBankBadge")}
              </span>
            )}
            {bankName && (
              <span
                className="rounded-full border border-state-financed/30 bg-state-financed/8 px-3 py-1 font-mono text-[10px] text-state-financed cursor-help"
                title={t("ucp600Article")}
              >
                {t("presentationWindowPrefix")} {presentationPeriodDays} {t("presentationWindowSuffix")}
              </span>
            )}
            {lc.evidenceAnchor?.hcsTopicId && (
              <span className="font-mono text-[10px] text-orbitra-teal-soft">
                {t("orbitraLedgerPrefix")} <span dir="ltr">{lc.evidenceAnchor.hcsTopicId}</span>
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="grid flex-1 grid-cols-2 gap-px overflow-hidden rounded-lg border border-state-financed/40 bg-state-financed/30">
        <Pane
          chip={sellerLabel}
          headline={`${bankName ? t("paneHeadlineLcReceived") : t("paneHeadlineEscrowReceived")} ${lc.amountUsd.toLocaleString()}`}
          detail={`${t("paneDetailExpiry")} ${lc.expiry} · ${bankName ? `${fundingPct}% ${bankName} ${t("paneDetailGuaranteeSuffix")}` : t("paneDetailFullyCollateralised")} · ${t("paneDetailSettlementArmed")}`}
          tone="finance"
          handshakeNumber={handshakeNumber}
        />
        <Pane
          chip={buyerLabel}
          headline={bankName ? `${t("paneHeadlineLcInstructed")} ${fundingPct}% ${bankName}` : t("paneHeadlineEscrowInstructed")}
          detail={
            bankName
              ? `${fundingPct}% ${t("paneDetailPartialFinancingSuffix")} ${bankName}${t("paneDetailRemainingBuyer")}`
              : t("paneDetailNoBankInvolved")
          }
          tone="finance"
          handshakeNumber={handshakeNumber}
        />
      </div>

      <div className="glass flex items-center gap-3 px-4 py-3 text-[11px]">
        <span className="font-mono text-[9px] font-bold uppercase tracking-[0.18em] text-state-financed">{t("mercatorLabel")}</span>
        <span className="text-orbitra-paper">"{bankName ? t("mercatorQuoteLcPrefix") : t("mercatorQuoteEscrowPrefix")} <span className="font-mono">{t("triggerCustomsCleared")}</span>."</span>
      </div>

      {/* A6 — vault visual reads as "payment locked" on bank-LC scenarios.
       *  Suppressed for Programmable Escrow (T1) since there's no bank.
       *  When the bank vault is shown, network + vault sit side-by-side
       *  so the screen doesn't run out of vertical space below the
       *  split-pane panes. T1 keeps the network full-width. */}
      {graph && (
        bankName ? (
          <div className="grid grid-cols-2 gap-4">
            <NetworkVignette graph={graph} preset={pickLcPreset(bankName)} />
            <VaultIllustration bankBadge={bankName} />
          </div>
        ) : (
          <NetworkVignette graph={graph} preset={pickLcPreset(bankName)} />
        )
      )}

      <BankerEvidence
        variant="lc-issued"
        amountUsd={lc.amountUsd}
        bankBadge={bankName}
        advisingBank={resolvedAdvisingBank}
      />

      {showDigitalLcModal && bankName && (
        <>
          <button
            type="button"
            onClick={() => setDigitalLcOpen(true)}
            className="self-start rounded border border-state-financed/40 px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-state-financed hover:bg-state-financed/10"
          >
            {t("viewDigitalLcButton")}
          </button>
          <DigitalLcModal
            open={digitalLcOpen}
            onClose={() => setDigitalLcOpen(false)}
            lcReference={lc.lcReference}
            amountUsd={lc.amountUsd}
            expiry={lc.expiry}
            issuingBank={bankName}
            advisingBank={resolvedAdvisingBank}
            confirmingBank={confirmingBank}
            applicantName={buyerLabel}
            beneficiaryName={sellerLabel}
            presentationPeriodDays={presentationPeriodDays}
            partialFinancingPct={fundingPct ?? undefined}
          />
        </>
      )}

      <LcIssuedSuccessPopup lcReference={lc.lcReference} isEscrow={!bankName} />
    </div>
  );
}

/**
 * Final success popup — surfaces once when LC issuance completes
 * ("LC issued successfully. Payment locked. Congratulations 🎉").
 * Auto-opens shortly after mount; click anywhere or press OK to dismiss.
 * On Programmable Escrow (T1, no bank) the headline reframes as
 * "Escrow issued successfully."
 */
function LcIssuedSuccessPopup({
  lcReference,
  isEscrow,
}: {
  lcReference: string;
  isEscrow: boolean;
}) {
  const tLC = useTranslations("lcIssued");
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setOpen(true), 900);
    return () => clearTimeout(t);
  }, []);
  if (!open) return null;
  const headline = isEscrow ? tLC("headlineEscrow") : tLC("headlineLc");
  return (
    <div
      className="absolute inset-0 z-40 grid place-items-center bg-orbitra-ink/70 backdrop-blur-sm"
      onClick={() => setOpen(false)}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="lc-issued-success-title"
        className="relative w-[min(520px,92vw)] rounded-md border-2 border-state-financed bg-orbitra-ink/95 p-5 text-center shadow-[0_0_60px_var(--state-financed-glow),0_0_18px_var(--state-financed)] backdrop-blur-md"
        onClick={(e) => e.stopPropagation()}
      >
        <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-state-financed">
          {isEscrow ? tLC("successKickerEscrow") : tLC("successKickerLc")}
        </p>
        <h3 id="lc-issued-success-title" className="mt-2 font-display text-xl font-bold text-orbitra-paper">
          {headline}
        </h3>
        <p className="mt-2 font-display text-base font-semibold text-state-financed">
          {tLC("paymentLocked")}
        </p>
        <p className="mt-2 font-mono text-[10px] text-orbitra-muted">
          ref · {lcReference}
        </p>
        <button
          type="button"
          autoFocus
          onClick={() => setOpen(false)}
          className="cta-primary mt-4 w-full py-2.5 font-display text-sm font-semibold"
        >
          {tLC("okButton")}
        </button>
      </div>
    </div>
  );
}

function pickLcPreset(bankBadge: "FAB" | "Itau" | null): Preset {
  if (bankBadge === "FAB")  return "lc-fab-financed";
  if (bankBadge === "Itau") return "lc-itau-financed";
  return "lc-self-financed";
}

function Pane({
  chip,
  headline,
  detail,
  tone,
  handshakeNumber,
}: { chip: string; headline: string; detail: string; tone: "finance"; handshakeNumber?: number }) {
  return (
    <div className="relative bg-orbitra-ink/85 p-5">
      <span className="absolute start-3 top-3 font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">{chip}</span>
      <div className="mt-6">
        <p className={clsx(
          "font-display text-base font-semibold",
          tone === "finance" && "text-state-financed",
        )}>{headline}</p>
        <p className="mt-2 text-[11px] text-orbitra-teal-soft">{detail}</p>
      </div>
      {handshakeNumber !== undefined && (
        <div className="absolute start-1/2 top-3 z-10 -translate-x-1/2 rounded-full bg-state-financed px-3 py-1 font-mono text-[10px] font-semibold text-orbitra-ink shadow-[0_0_24px_var(--state-financed-glow)]">
          Handshake #{handshakeNumber}
        </div>
      )}
    </div>
  );
}
